import React, { lazy, Suspense, useEffect, type ReactNode } from "react";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getAuth } from "../stores/slices/Auth";

const NewCustomerRegister = lazy(() => import("../pages/NewCustomerRegister"));
const Home = lazy(() => import("../pages/Home"));
const Customer = lazy(() => import("../pages/Customer/Index"));
const User = lazy(() => import("../pages/User/Index"));
const CreateUser = lazy(() => import("../pages/User/Create"));
const NewCustomer = lazy(() => import("../pages/NewCustomer/Index"));
const CreateCustomerDetail = lazy(() => import("../pages/Customer/Create"));
const LoginPage = lazy(() => import("../pages/Login/Index"));
const ChangePasswordPage = lazy(() => import("../pages/ChangePassword/Index"));
const PrivateRoute = lazy(() => import("./privateRoute"));
const UserDetail = lazy(() => import("../pages/User/[id]"));
const BusinessInformation = lazy(
  () => import("../pages/Setting/BusinessInformation"),
);
const SetUpDocuments = lazy(() => import("../pages/Setting/SetUpDocuments"));
const ProductCategory = lazy(
  () => import("../pages/SettingProductCategory/Index"),
);
const ProductCategoryDetail = lazy(
  () => import("../pages/SettingProductCategory/[id]"),
);
const ProductCategoryCreate = lazy(
  () => import("../pages/SettingProductCategory/Create"),
);
const CustomerDetail = lazy(() => import("../pages/Customer/[id]"));
const TruckRoute = lazy(() => import("../pages/Setting/TruckRoute"));
const CustomerSchedulesPage = lazy(
  () => import("../pages/Customer/CustomerSchedules"),
);
const TruckPage = lazy(() => import("../pages/Truck/Index"));
const CreateTruck = lazy(() => import("../pages/Truck/Create_new"));
const TruckDetailPage = lazy(() => import("../pages/Truck/[id]"));
const RoutePage = lazy(() => import("../pages/Route/Index"));
const CreateRoute = lazy(() => import("../pages/Route/Create"));
const RouteDetailPage = lazy(() => import("../pages/Route/[id]"));
const Product = lazy(() => import("../pages/SettingProduct/Index"));
const ProductDetail = lazy(() => import("../pages/SettingProduct/[id]"));
const RouteWay = lazy(() => import("../pages/RouteWay"));
const ProductCreate = lazy(() => import("../pages/SettingProduct/Create"));
const CustomerProductsPrice = lazy(
  () => import("../pages/Customer/CustomerProductsPrice"),
);
const ContractIndex = lazy(() => import("../pages/Setting/Contract/Index"));
const CategoryType = lazy(() => import("../pages/SettingCategoryType/Index"));
const CategoryTypeCreate = lazy(
  () => import("../pages/SettingCategoryType/Create"),
);
const CategoryTypeDetail = lazy(
  () => import("../pages/SettingCategoryType/[id]"),
);
const BusinessType = lazy(() => import("../pages/SettingBusinessType/Index"));
const BusinessTypeCreate = lazy(
  () => import("../pages/SettingBusinessType/Create"),
);
const BusinessTypeDetail = lazy(
  () => import("../pages/SettingBusinessType/[id]"),
);
const Text = lazy(() => import("../pages/test"));
const GarbageWeight = lazy(() => import("../pages/GarbageWeight"));
const CreateGarbageWeight = lazy(() => import("../pages/GarbageWeight/Create"));
const GarbageWeightDetail = lazy(() => import("../pages/GarbageWeight/[id]"));
const ContractDetail = lazy(() => import("../pages/Setting/Contract/[id]"));
const CreateContractTemplate = lazy(
  () => import("../pages/Setting/Contract/Create"),
);
const QuotationCreate = lazy(
  () => import("../pages/SalesDocument/Quotation/Create"),
);
const Quotation = lazy(() => import("../pages/SalesDocument/Quotation/index"));
const Contract = lazy(() => import("../pages/Contract"));
const ContractCreate = lazy(() => import("../pages/Contract/Create"));
const GarbageTransfer = lazy(() => import("../pages/GarbageTransfer"));
const GarbageSendKiln = lazy(() => import("../pages/GarbageSendKiln"));
const GarbageSendKilnDetail = lazy(
  () => import("../pages/GarbageSendKiln/[id]"),
);
const PageModalRobin = lazy(() => import("../pages/modal_robin"));
const EditContract = lazy(() => import("../pages/Contract/[id]"));
const GeneratePDF = lazy(() => import("../pages/Contract/GeneratePDF"));
const QuotationUpdate = lazy(
  () => import("../pages/SalesDocument/Quotation/[id]"),
);
const BillingInvoice = lazy(
  () => import("../pages/SalesDocument/BillingInvoice"),
);
const BillingInvoiceCreate = lazy(
  () => import("../pages/SalesDocument/BillingInvoice/Create"),
);
const BillingInvoiceUpdate = lazy(
  () => import("../pages/SalesDocument/BillingInvoice/[id]"),
);
const DebitNote = lazy(() => import("../pages/SalesDocument/DebitNote"));
const DebitNoteCreate = lazy(
  () => import("../pages/SalesDocument/DebitNote/Create"),
);
const DebitNoteUpdate = lazy(
  () => import("../pages/SalesDocument/DebitNote/[id]"),
);
const CreditNote = lazy(() => import("../pages/SalesDocument/CreditNote"));
const CreditNoteCreate = lazy(
  () => import("../pages/SalesDocument/CreditNote/Create"),
);
const CreditNoteUpdate = lazy(
  () => import("../pages/SalesDocument/CreditNote/[id]"),
);
const TaxAdjustmentNotePDF = lazy(
  () => import("../pages/SalesDocument/TaxAdjustmentNotePDF"),
);
const WithholdingTax = lazy(() => import("../pages/WithholdingTax/Index"));
const WithholdingTaxCreate = lazy(
  () => import("../pages/WithholdingTax/Create"),
);
const WithholdingTaxUpdate = lazy(() => import("../pages/WithholdingTax/[id]"));
const WithholdingTaxPDF = lazy(
  () => import("../pages/WithholdingTax/WithholdingTaxPDF"),
);
const PublicRoute = lazy(() => import("./publicRoute"));
const ViewContract = lazy(
  () => import("../pages/Public/Contract/ViewContract"),
);
const PreViewPDF = lazy(() => import("../pages/RouteWay/PreViewPDF"));
const PreViewTruckRoutePDF = lazy(
  () => import("../pages/Setting/TruckRoute/PreViewPDF"),
);
const QuotationPDF = lazy(
  () => import("../pages/SalesDocument/Quotation/QuotationPDF"),
);
const BillingInvoicePDF = lazy(
  () => import("../pages/SalesDocument/BillingInvoice/BillingInvoicePDF"),
);
const Receipt = lazy(() => import("../pages/SalesDocument/Receipt"));
const ReceiptCreate = lazy(
  () => import("../pages/SalesDocument/Receipt/create"),
);
const ReceiptUpdate = lazy(() => import("../pages/SalesDocument/Receipt/[id]"));
const ReceiptPDF = lazy(
  () => import("../pages/SalesDocument/Receipt/ReceiptPDF"),
);
const BankAccountIndex = lazy(
  () => import("../pages/SettingBankAccount/Index"),
);
const BankAccountCreate = lazy(
  () => import("../pages/SettingBankAccount/Create"),
);
const BankAccountDetail = lazy(
  () => import("../pages/SettingBankAccount/[id]"),
);
const NotificationPage = lazy(() => import("../pages/Notification"));
const W8review = lazy(() => import("../pages/GarbageWeight/w8review"));
const GarbageWeightConfirm = lazy(
  () => import("../pages/GarbageWeight/Confirm"),
);
const Supplier = lazy(() => import("../pages/Supplier"));
const SupplierCreate = lazy(() => import("../pages/Supplier/Create"));
const SupplierEdit = lazy(() => import("../pages/Supplier/Edit"));
const Expense = lazy(() => import("../pages/Expense/Index"));
const ExpenseCreate = lazy(() => import("../pages/Expense/Create"));
const ExpenseEdit = lazy(() => import("../pages/Expense/[id]"));
const CMSIndex = lazy(() => import("../pages/CMS/Index"));
const CMSCreateEdit = lazy(() => import("../pages/CMS/Edit"));
const PurchaseDocument = lazy(() => import("../pages/PurchaseDocument/Index"));
const PurchaseDocumentCreate = lazy(
  () => import("../pages/PurchaseDocument/Create"),
);
const PurchaseDocumentEdit = lazy(
  () => import("../pages/PurchaseDocument/[id]"),
);
const PurchaseDocumentPDF = lazy(
  () => import("../pages/PurchaseDocument/PurchasePDF"),
);
const ExpenseCategoryCreate = lazy(
  () => import("../pages/Setting/Expense/Category/Create"),
);
const ExpenseCategoryEdit = lazy(
  () => import("../pages/Setting/Expense/Category/Edit"),
);
const ExpenseCategory = lazy(() => import("../pages/Setting/Expense/Category"));
const ExpenseItem = lazy(() => import("../pages/Setting/Expense/Item"));
const ExpenseItemCreate = lazy(
  () => import("../pages/Setting/Expense/Item/Create"),
);
const ExpenseItemEdit = lazy(
  () => import("../pages/Setting/Expense/Item/Edit"),
);
const BillingInvoiceReport = lazy(
  () => import("../pages/Report/BillingInvoiceReport"),
);
const ReceiptCollectionReport = lazy(
  () => import("../pages/Report/ReceiptCollectionReport"),
);
const ChequeCollectionReport = lazy(
  () => import("../pages/Report/ChequeCollectionReport"),
);
const ReceiptWithholdingTaxReport = lazy(
  () => import("../pages/Report/ReceiptWithholdingTaxReport"),
);
const PND3PND53Report = lazy(
  () => import("../pages/Report/PND3PND53Report"),
);
const AnnualRevenueCollectionsReport = lazy(
  () => import("../pages/Report/AnnualRevenueCollectionsReport"),
);
const MonthlyCustomerWasteReport = lazy(
  () => import("../pages/Report/MonthlyCustomerWasteReport"),
);
const AnnualCustomerWasteReport = lazy(
  () => import("../pages/Report/AnnualCustomerWasteReport"),
);
const ProvinceWasteSummaryReport = lazy(
  () => import("../pages/Report/ProvinceWasteSummaryReport"),
);
const DailyWasteAverageReport = lazy(
  () => import("../pages/Report/DailyWasteAverageReport"),
);
const WasteReceiptReport = lazy(
  () => import("../pages/Report/WasteReceiptReport"),
);
const CollectedVsSoldReport = lazy(
  () => import("../pages/Report/CollectedVsSoldReport"),
);
const DailyTruckWasteReport = lazy(
  () => import("../pages/Report/DailyTruckWasteReport"),
);
const GarbageTransferDailyReport = lazy(
  () => import("../pages/Report/GarbageTransferDailyReport"),
);
const BigTruckStockReport = lazy(
  () => import("../pages/Report/BigTruckStockReport"),
);
const WasteGrowthTrendReport = lazy(
  () => import("../pages/Report/WasteGrowthTrendReport"),
);
const CustomerContractReport = lazy(
  () => import("../pages/Report/CustomerContractReport"),
);
const AllCustomersReport = lazy(
  () => import("../pages/Report/AllCustomersReport"),
);
const CancelledClinicsReport = lazy(
  () => import("../pages/Report/CancelledClinicsReport"),
);
const NewCustomers30DaysReport = lazy(
  () => import("../pages/Report/NewCustomers30DaysReport"),
);
const CustomerWithoutRoute = lazy(
  () => import("../pages/Report/CustomerWithoutRoute"),
);
const BusinessReportPage = lazy(
  () => import("../pages/Report/BusinessReportPage"),
);
const CustomerCancle = lazy(() => import("../pages/Customer/Cancle"));
const CustomerSuspendCollect = lazy(
  () => import("../pages/Customer/SuspendCollect"),
);

const RouteFallback = () => (
  <div
    className="flex items-center justify-center px-4 py-6 text-sm font-medium text-gray-500 dark:text-gray-300"
    role="status"
    aria-live="polite"
  >
    กำลังโหลด...
  </div>
);

const RouteBoundary = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<RouteFallback />}>{children}</Suspense>
);

const Web = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAuth());
  }, [dispatch]);

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/test"
          element={
            <RouteBoundary>
              <Text />
            </RouteBoundary>
          }
        />
        <Route
          path="/exampleModal"
          element={
            <RouteBoundary>
              <PageModalRobin />
            </RouteBoundary>
          }
        />
        <Route
          path="/login"
          element={
            <RouteBoundary>
              <LoginPage />
            </RouteBoundary>
          }
        />
        <Route
          element={
            <RouteBoundary>
              <PublicRoute />
            </RouteBoundary>
          }
        >
          <Route path="/register" element={<NewCustomerRegister />} />
          <Route path="/documents">
            <Route path="contract/:token" element={<ViewContract />} />
          </Route>
        </Route>
        <Route
          path="/route-way/preview"
          element={
            <RouteBoundary>
              <PreViewPDF />
            </RouteBoundary>
          }
        />
        <Route
          path="/setting/truck-route/preview"
          element={
            <RouteBoundary>
              <PreViewTruckRoutePDF />
            </RouteBoundary>
          }
        />
        <Route
          element={
            <RouteBoundary>
              <PrivateRoute />
            </RouteBoundary>
          }
        >
          <Route path="/" element={<Home />} />
          <Route path="/users" element={<User />} />
          <Route path="/create-users" element={<CreateUser />} />
          <Route path="/users/:id" element={<UserDetail />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />

            <Route path="/new-customers" element={<NewCustomer />} />
            <Route path="/customers" element={<Customer />} />
            <Route path="/customer">
              <Route path="create" element={<CreateCustomerDetail />} />
              <Route path=":id" element={<CustomerDetail />} />
              <Route path="schedule/:id" element={<CustomerSchedulesPage />} />
              <Route path="product/:id" element={<CustomerProductsPrice />} />
            </Route>
            <Route path="/customers/cancle" element={<CustomerCancle />} />
            <Route
              path="/customers/suspend-collect"
              element={<CustomerSuspendCollect />}
            />

            <Route path="/quotation">
              <Route path="" element={<Quotation />} />
              <Route path="create" element={<QuotationCreate />} />
              <Route path="edit/:id" element={<QuotationUpdate />} />
              <Route path=":id" element={<QuotationPDF />} />
            </Route>

            <Route path="/billing-invoice">
              <Route path="" element={<BillingInvoice />} />
              <Route path="create" element={<BillingInvoiceCreate />} />
              <Route path="edit/:id" element={<BillingInvoiceUpdate />} />
              <Route path=":id" element={<BillingInvoicePDF />} />
              <Route path="pdf/:id" element={<BillingInvoicePDF />} />
            </Route>

            <Route path="/receipt">
              <Route path="" element={<Receipt />} />
              <Route path="create" element={<ReceiptCreate />} />
              <Route path="create/:id" element={<ReceiptCreate />} />
              <Route path="edit/:id" element={<ReceiptUpdate />} />
              <Route path=":id" element={<ReceiptPDF />} />
              <Route path="pdf/:id" element={<ReceiptPDF />} />
            </Route>

            <Route
              path="/tax-invoice/*"
              element={<Navigate to="/receipt" replace />}
            />

            <Route path="/credit-note">
              <Route path="" element={<CreditNote />} />
              <Route path="create" element={<CreditNoteCreate />} />
              <Route
                path="pdf/:id"
                element={<TaxAdjustmentNotePDF noteType="credit" />}
              />
              <Route path=":id" element={<CreditNoteUpdate />} />
            </Route>

            <Route path="/debit-note">
              <Route path="" element={<DebitNote />} />
              <Route path="create" element={<DebitNoteCreate />} />
              <Route
                path="pdf/:id"
                element={<TaxAdjustmentNotePDF noteType="debit" />}
              />
              <Route path=":id" element={<DebitNoteUpdate />} />
            </Route>

            <Route path="/route-way">
              <Route path="" element={<RouteWay />} />
            </Route>

            <Route path="/contracts" element={<Contract />} />
            <Route path="/contract">
              <Route path="create" element={<ContractCreate />} />
              <Route path=":id" element={<EditContract />} />
              <Route path="pdf/:id" element={<GeneratePDF />} />
            </Route>

            <Route path="/withholding-tax">
              <Route path="" element={<WithholdingTax />} />
              <Route path="create" element={<WithholdingTaxCreate />} />
              <Route path="print/:documentNo" element={<WithholdingTaxPDF />} />
              <Route path=":id" element={<WithholdingTaxUpdate />} />
            </Route>

            <Route path="/garbage">
              <Route path="" element={<W8review />} />
              <Route path="weight" element={<GarbageWeight />} />
              <Route path="create-weight" element={<CreateGarbageWeight />} />
              <Route path="weight/:id" element={<GarbageWeightDetail />} />
              <Route path="confirm/:id" element={<GarbageWeightConfirm />} />

              <Route path="send-kiln" element={<GarbageSendKiln />} />
              {/* <Route
              path="create-send-kiln"
              element={<CreateGarbageSendKiln />}
            /> */}
              <Route path="send-kiln/:id" element={<GarbageSendKilnDetail />} />
              <Route path="transfer" element={<GarbageTransfer />} />
            </Route>

            <Route path="/notification" element={<NotificationPage />} />

            <Route path="purchase-document">
              <Route path="" element={<PurchaseDocument />} />
              <Route path="create" element={<PurchaseDocumentCreate />} />
              <Route path="edit/:id" element={<PurchaseDocumentEdit />} />
              <Route path=":id" element={<PurchaseDocumentPDF />} />
            </Route>

            <Route path="expense">
              <Route path="" element={<Expense />} />
              <Route path="create" element={<ExpenseCreate />} />
              <Route path="edit/:id" element={<ExpenseEdit />} />
            </Route>

          <Route path="cms">
            <Route path="" element={<CMSIndex />} />
            <Route path="create" element={<CMSCreateEdit />} />
            <Route path="edit/:id" element={<CMSCreateEdit />} />
          </Route>

            <Route path="/reports">
              <Route
                index
                element={<Navigate to="/reports/billing-invoices" replace />}
              />
              <Route
                path="business/collections"
                element={<ReceiptCollectionReport />}
              />
              <Route
                path="cheque-collections"
                element={<ChequeCollectionReport />}
              />
              <Route
                path="receipt-withholding-tax"
                element={<ReceiptWithholdingTaxReport />}
              />
              <Route path="pnd3-pnd53" element={<PND3PND53Report />} />
              <Route
                path="annual-revenue-collections"
                element={<AnnualRevenueCollectionsReport />}
              />
              <Route
                path="business/:reportKey"
                element={<BusinessReportPage />}
              />
              <Route
                path="customers-without-route"
                element={<CustomerWithoutRoute />}
              />
              <Route
                path="customer-contracts"
                element={<CustomerContractReport />}
              />
              <Route path="all-customers" element={<AllCustomersReport />} />
              <Route path="cancelled-clinics" element={<CancelledClinicsReport />} />
              <Route
                path="new-customers-30-days"
                element={<NewCustomers30DaysReport />}
              />
              <Route
                path="billing-invoices"
                element={<BillingInvoiceReport />}
              />
              <Route
                path="monthly-customer-waste"
                element={<MonthlyCustomerWasteReport />}
              />
              <Route
                path="annual-customer-waste"
                element={<AnnualCustomerWasteReport />}
              />
              <Route
                path="province-waste-summary"
                element={<ProvinceWasteSummaryReport />}
              />
              <Route
                path="daily-waste-average"
                element={<DailyWasteAverageReport />}
              />
              <Route path="waste-receipt" element={<WasteReceiptReport />} />
              <Route
                path="collected-vs-sold"
                element={<CollectedVsSoldReport />}
              />
              <Route
                path="daily-truck-waste"
                element={<DailyTruckWasteReport />}
              />
              <Route
                path="garbage-transfer-daily"
                element={<GarbageTransferDailyReport />}
              />
              <Route
                path="big-truck-stock"
                element={<BigTruckStockReport />}
              />
              <Route
                path="waste-growth-trend"
                element={<WasteGrowthTrendReport />}
              />
            </Route>

            <Route path="/setting">
              <Route
                path="businessInformation"
                element={<BusinessInformation />}
              />
              <Route path="setupdoc" element={<SetUpDocuments />} />
              <Route
                path="create-product-category"
                element={<ProductCategoryCreate />}
              />
              <Route path="product-category" element={<ProductCategory />} />
              <Route
                path="product-category/:id"
                element={<ProductCategoryDetail />}
              />
              <Route path="product" element={<Product />} />
              <Route path="create-product" element={<ProductCreate />} />
              <Route path="product/:id" element={<ProductDetail />} />

              <Route path="category-type" element={<CategoryType />} />
              <Route
                path="create-category-type"
                element={<CategoryTypeCreate />}
              />
              <Route
                path="category-type/:id"
                element={<CategoryTypeDetail />}
              />
              <Route path="business-type" element={<BusinessType />} />
              <Route
                path="create-business-type"
                element={<BusinessTypeCreate />}
              />
              <Route
                path="business-type/:id"
                element={<BusinessTypeDetail />}
              />
              <Route path="create-users" element={<CreateUser />} />
              <Route path="truck" element={<TruckPage />} />
              <Route path="create-trucks" element={<CreateTruck />} />
              <Route path="truck/:id" element={<TruckDetailPage />} />
              <Route path="truck-route" element={<TruckRoute />} />
              <Route path="route" element={<RoutePage />} />
              <Route path="create-routes" element={<CreateRoute />} />
              <Route path="route/:id" element={<RouteDetailPage />} />
              <Route path="contracts" element={<ContractIndex />} />
              <Route path="contract">
                <Route path="create" element={<CreateContractTemplate />} />
                <Route path=":id" element={<ContractDetail />} />
              </Route>
              <Route path="bank-account" element={<BankAccountIndex />} />
              <Route
                path="create-bank-account"
                element={<BankAccountCreate />}
              />
              <Route path="bank-account/:id" element={<BankAccountDetail />} />
              <Route path="supplier" element={<Supplier />} />
              <Route path="expense">
                <Route path="suppliers">
                  <Route path="" element={<Supplier />} />
                  <Route path="create" element={<SupplierCreate />} />
                  <Route path="edit/:id" element={<SupplierEdit />} />
                </Route>
                <Route path="categories">
                  <Route path="" element={<ExpenseCategory />} />
                  <Route path="create" element={<ExpenseCategoryCreate />} />
                  <Route path="edit/:id" element={<ExpenseCategoryEdit />} />
                </Route>
                <Route path="item">
                  <Route path="" element={<ExpenseItem />} />
                  <Route path="create" element={<ExpenseItemCreate />} />
                  <Route path="edit/:id" element={<ExpenseItemEdit />} />
                </Route>
              </Route>
            </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default Web;
